import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, json } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { ErrorBanner } from '../../components/ErrorBanner';
import { AddModal } from '../../components/AddModal';
import { useScope } from '../../context/ScopeContext';

export function ServiceDetailPage() {
  const { id = '' } = useParams();
  const scope = useScope();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['service', id], queryFn: () => api<any>(`/catalog/services/${id}`), enabled: Boolean(id) });
  const prices = useQuery({ queryKey: ['service-prices', id], queryFn: () => api<any[]>(`/catalog/services/${id}/prices`), enabled: Boolean(id) });
  const [plCode, setPlCode] = useState('');
  const [plName, setPlName] = useState('');
  const [priceListId, setPriceListId] = useState('');
  const [kind, setKind] = useState<'SINGLE' | 'PACKAGE'>('SINGLE');
  const [amount, setAmount] = useState('0.0000');
  const [sessionsCount, setSessionsCount] = useState(1);
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState('');
  const [optionCode, setOptionCode] = useState('');
  const [optionName, setOptionName] = useState('');
  const refresh = () => { qc.invalidateQueries({ queryKey: ['service', id] }); qc.invalidateQueries({ queryKey: ['service-prices', id] }); };
  const pl = useMutation({ mutationFn: () => api(`/catalog/services/${id}/price-lists`, { method: 'POST', ...json({ companyId: q.data.companyId, serviceId: id, code: plCode, name: plName, active: true }) }), onSuccess: () => { setPlCode(''); setPlName(''); refresh(); } });
  const price = useMutation({ mutationFn: () => api(`/catalog/services/${id}/prices`, { method: 'POST', ...json({ priceListId, serviceId: id, companyId: q.data.companyId, branchId: scope.branchId || null, kind, amount, sessionsCount, effectiveFrom: from, effectiveTo: to || null, active: true }) }), onSuccess: refresh });
  const option = useMutation({ mutationFn: () => api(`/catalog/services/${id}/options`, { method: 'POST', ...json({ code: optionCode, name: optionName, active: true, sortOrder: 0 }) }), onSuccess: () => { setOptionCode(''); setOptionName(''); refresh(); } });
  const x = q.data;
  const lists = x?.priceLists ?? [];
  const rows = useMemo(() => prices.data ?? [], [prices.data]);

  return <section>
    <PageHeader title={x?.name ?? 'Service'} subtitle={x ? `${x.code} • ${x.category?.name}` : 'Service pricing'} actions={x ? <>
      <AddModal title="Add price list" buttonLabel="+ Add price list" submitLabel="Add price list" error={pl.error} onSubmit={() => pl.mutateAsync()}>
        <label>Code<input value={plCode} onChange={e => setPlCode(e.target.value)} required /></label>
        <label>Name<input value={plName} onChange={e => setPlName(e.target.value)} required /></label>
      </AddModal>
      <AddModal title="Add price version" buttonLabel="+ Add price" submitLabel="Add historical version" error={price.error} onSubmit={() => price.mutateAsync()} width="wide">
        <label>Price list<select value={priceListId} onChange={e => setPriceListId(e.target.value)} required><option value="">Price list…</option>{lists.map((p: any) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}</select></label>
        <label>Kind<select value={kind} onChange={e => setKind(e.target.value as any)}><option>SINGLE</option><option>PACKAGE</option></select></label>
        <label>Amount<input inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} required /></label>
        <label>Sessions<input type="number" min="1" value={sessionsCount} onChange={e => setSessionsCount(Number(e.target.value))} /></label>
        <label>Effective from<input type="date" value={from} onChange={e => setFrom(e.target.value)} required /></label>
        <label>Effective to<input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
      </AddModal>
      <AddModal title="Add service option" buttonLabel="+ Add option" submitLabel="Add option" error={option.error} onSubmit={() => option.mutateAsync()}>
        <label>Code<input value={optionCode} onChange={e => setOptionCode(e.target.value)} required /></label>
        <label>Name<input value={optionName} onChange={e => setOptionName(e.target.value)} required /></label>
      </AddModal>
    </> : null} />
    <Link to="/catalog/services">← Back to services</Link>
    <ErrorBanner error={q.error || prices.error || pl.error || price.error || option.error} />
    {x && <>
      <div className="card"><h3>Price history</h3><DataTable rows={rows} columns={[
        { key: 'list', header: 'Price list', render: (r: any) => r.priceList?.name },
        { key: 'kind', header: 'Kind', render: (r: any) => r.kind },
        { key: 'amount', header: 'Amount', render: (r: any) => String(r.amount) },
        { key: 'branch', header: 'Branch', render: (r: any) => r.branch?.name ?? 'All branches' },
        { key: 'from', header: 'From', render: (r: any) => String(r.effectiveFrom).slice(0, 10) },
        { key: 'to', header: 'To', render: (r: any) => r.effectiveTo ? String(r.effectiveTo).slice(0, 10) : 'Open' },
      ]} /></div>
      <div className="card"><h3>Options</h3>{x.options?.map((o: any) => <span className="pill" key={o.id}>{o.code} — {o.name}</span>)}</div>
    </>}
  </section>;
}
